import { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { listAll, listByRange } from '../../db/entries';
import { listAllDailies, listDailiesByRange } from '../../db/dailies';
import { getCategoryMap, listCategories } from '../../db/categories';
import { emptyProjects, getProjectsDoc, listProjects } from '../../db/projects';
import { useAsync } from '../hooks/useAsync';
import { useDataVersion } from '../../db/events';
import { EntryBadge } from '../components/EntryBadge';
import { MediaImage } from '../components/MediaImage';
import { Highlight } from '../components/Highlight';
import { excerpt, matchesAll, parseQuery } from '../../lib/text';
import { buildCsv, csvFileName, downloadCsv } from '../../lib/csv';
import { addMonths, formatTime, toDateKey } from '../../lib/datetime';
import type { Category, ProjectsDoc } from '../../domain/types';
import type { DailyRecord, EntryRecord } from '../../db/schema';

type Preset = { label: string; from: string; to: string };

function presets(): Preset[] {
  const now = new Date();
  const first = (d: Date) => toDateKey(new Date(d.getFullYear(), d.getMonth(), 1));
  const last = (d: Date) => toDateKey(new Date(d.getFullYear(), d.getMonth() + 1, 0));
  const prev = addMonths(now, -1);
  return [
    { label: '今月', from: first(now), to: last(now) },
    { label: '先月', from: first(prev), to: last(prev) },
    { label: '今年', from: `${now.getFullYear()}-01-01`, to: `${now.getFullYear()}-12-31` },
    { label: '全期間', from: '', to: '' },
  ];
}

/** 検索（仕様書 §13）。すべてローカルのIndexedDBに対して実行する。 */
export function SearchView() {
  const version = useDataVersion();
  const [params, setParams] = useSearchParams();
  const q = params.get('q') ?? '';
  const cat = params.get('cat') ?? '';
  const from = params.get('from') ?? '';
  const to = params.get('to') ?? '';
  const asc = params.get('sort') === 'asc';

  // 入力は250msデバウンスして検索する
  const [draftQ, setDraftQ] = useState(q);
  useEffect(() => setDraftQ(q), [q]);
  useEffect(() => {
    if (draftQ === q) return;
    const id = setTimeout(() => update('q', draftQ), 250);
    return () => clearTimeout(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draftQ]);

  const categories = useAsync<Map<string, Category>>(getCategoryMap, [version], new Map());
  const projects = useAsync<ProjectsDoc>(getProjectsDoc, [version], emptyProjects());
  const catList = useAsync<Category[]>(() => listCategories(), [version], []);
  const projectList = listProjects(projects);
  const terms = useMemo(() => parseQuery(q), [q]);

  const results = useAsync<EntryRecord[]>(
    async () => {
      const base = from && to ? await listByRange(from, to) : await listAll();
      return base
        .filter((e) => (terms.length === 0 ? true : matchesAll(e.searchText, terms)))
        .filter((e) => {
          if (!cat) return true;
          if (cat.startsWith('p:')) return e.projectId === cat.slice(2);
          if (cat === '__none__') {
            if (e.projectId) return false;
            if (!e.categoryId) return true;
            const c = categories.get(e.categoryId);
            return !c || c.deleted;
          }
          return e.categoryId === cat && !e.projectId;
        })
        .sort((a, b) => (a.date < b.date ? (asc ? -1 : 1) : asc ? 1 : -1));
    },
    [q, cat, from, to, asc, version, categories],
    [],
  );

  // 一日のまとめも同じ条件で絞り込む（カテゴリ指定時は対象外）
  const dailies = useAsync<DailyRecord[]>(
    async () => {
      if (cat) return [];
      const base = from && to ? await listDailiesByRange(from, to) : await listAllDailies();
      return base
        .filter((d) => (terms.length === 0 ? false : matchesAll(d.searchText, terms)))
        .sort((a, b) => (a.date < b.date ? (asc ? -1 : 1) : asc ? 1 : -1));
    },
    [q, cat, from, to, asc, version],
    [],
  );

  const [exporting, setExporting] = useState(false);

  const update = (k: string, v: string) => {
    const next = new URLSearchParams(params);
    if (v) next.set(k, v);
    else next.delete(k);
    setParams(next, { replace: true });
  };

  const applyPreset = (p: Preset) => {
    const next = new URLSearchParams(params);
    if (p.from) {
      next.set('from', p.from);
      next.set('to', p.to);
    } else {
      next.delete('from');
      next.delete('to');
    }
    setParams(next, { replace: true });
  };

  const onExport = () => {
    setExporting(true);
    try {
      downloadCsv(buildCsv(results, categories), csvFileName(results));
    } finally {
      setExporting(false);
    }
  };

  const activePreset = presets().find((p) => p.from === from && p.to === to);
  const hasFilter = q !== '' || cat !== '' || from !== '';

  return (
    <div className="view">
      <header className="view-head view-head-search">
        <input
          type="search"
          className="search-input"
          placeholder="タイトルと本文を検索"
          value={draftQ}
          onChange={(e) => setDraftQ(e.target.value)}
        />
        <div className="search-filters">
          <select value={cat} onChange={(e) => update('cat', e.target.value)}>
            <option value="">すべて</option>
            {projectList.length > 0 && (
              <optgroup label="プロジェクト">
                {projectList.map((p) => (
                  <option key={p.id} value={`p:${p.id}`}>
                    ◆ {p.name}
                  </option>
                ))}
              </optgroup>
            )}
            <optgroup label="カテゴリ">
              {catList.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </optgroup>
            <option value="__none__">未設定</option>
          </select>
          <input type="date" value={from} onChange={(e) => update('from', e.target.value)} aria-label="開始日" />
          <span>〜</span>
          <input type="date" value={to} onChange={(e) => update('to', e.target.value)} aria-label="終了日" />
        </div>
        <div className="search-presets">
          {presets().map((p) => (
            <button
              key={p.label}
              className={`chip${activePreset?.label === p.label ? ' is-on' : ''}`}
              onClick={() => applyPreset(p)}
            >
              {p.label}
            </button>
          ))}
          {hasFilter && (
            <button className="chip" onClick={() => setParams(new URLSearchParams(), { replace: true })}>
              条件をクリア
            </button>
          )}
        </div>
      </header>

      <div className="view-body">
        <div className="result-bar">
          <span className="result-count">
            {results.length.toLocaleString()}件
            {dailies.length > 0 && ` / まとめ ${dailies.length}件`}
          </span>
          <span className="spacer" />
          <button className="chip" onClick={() => update('sort', asc ? '' : 'asc')}>
            {asc ? '古い順 ↑' : '新しい順 ↓'}
          </button>
          <button className="chip" onClick={onExport} disabled={exporting || results.length === 0}>
            ⤓ CSVエクスポート
          </button>
        </div>

        {dailies.length > 0 && (
          <div className="card-list daily-results">
            {dailies.map((d) => (
              <Link key={d.date} to={`/day/${d.date}`} className="card">
                <div className="card-head">
                  <span className="card-time">{d.date}</span>
                  <span className="badge badge-daily">一日のまとめ</span>
                </div>
                {d.events && (
                  <div className="card-body">
                    <Highlight text={excerpt(d.events, terms)} terms={terms} />
                  </div>
                )}
                {d.good && (
                  <div className="card-body">
                    <Highlight text={excerpt(d.good, terms)} terms={terms} />
                  </div>
                )}
                {d.action && (
                  <div className="card-body">
                    <Highlight text={excerpt(d.action, terms)} terms={terms} />
                  </div>
                )}
              </Link>
            ))}
          </div>
        )}

        {results.length === 0 && dailies.length === 0 ? (
          <div className="empty">
            <p>該当する記録がありません</p>
          </div>
        ) : (
          <div className="card-list">
            {results.map((e) => (
              <Link key={e.id} to={`/entry/${e.id}`} className="card">
                <div className="card-head">
                  <span className="card-time">
                    {e.dateKey} {formatTime(e.date)}
                  </span>
                  <EntryBadge categoryId={e.categoryId} projectId={e.projectId} categories={categories} projects={projects} />
                  {e._dirty && <span className="card-dirty">↑</span>}
                </div>
                <div className="card-title">
                  <Highlight text={e.title || '（無題）'} terms={terms} />
                </div>
                {e.body && (
                  <div className="card-body">
                    <Highlight text={excerpt(e.body, terms)} terms={terms} />
                  </div>
                )}
                {e.media.length > 0 && (
                  <div className="card-thumbs">
                    {e.media.slice(0, 4).map((m) => (
                      <MediaImage key={m.id} media={m} month={e.monthKey} entryId={e.id} className="thumb" />
                    ))}
                    {e.media.length > 4 && <div className="thumb thumb-more">+{e.media.length - 4}</div>}
                  </div>
                )}
              </Link>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
